import React, { useEffect, useState } from 'react';
import usePageTitle from '../../../hooks/usePageTitle';
import { BaseQueryParams } from '../../../providers/Global.model';
import TwTable, { TwTableColumn, TwTableData } from '../../../components/TwTable/TwTable';
import TwPaginator from '../../../components/TwPaginator/TwPaginator';
import TWGenericMenu, {
  TwGenericMenuOptions
} from '../../../components/TwGenericMenu/TwGenericMenu';
import TwDialog from '../../../components/TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import { ReactComponent as WarningSign } from '../../../assets/warning-sign.svg';
import Box from '@mui/material/Box/Box';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import StringUtils from '../../../utils/stringUtils';
import TwBackdrop from '../../../components/TwBackdrop/TwBackdrop';
import { UserResponse, USER_ROLE } from '../../../providers/User/User.model';
import UserService from '../../../providers/User/User.service';
import { Characters, useUnformatValue } from '../../../hooks/useAllowCharacters';
import useAuth from '../../../providers/Auth/Auth.provider';
import { CarrierPayRateType, CarrierResponse } from '../../../redux/api/carriers/carriers.model';
import {
  useDeleteCarrierMutation,
  useGetCarriersQuery
} from '../../../redux/api/carriers/carrierApi';
import { logRocketTrack } from '../../../utils/logRocketUtils';
import './Carriers.scss';
import TwEditCompaniesModal from '../EditCompaniesModal/TwEditCompaniesModal';
import { CompaniesEntities } from '../Companies';

const Carriers: React.FC = (): JSX.Element => {
  usePageTitle('Carriers | Truckbase');
  const {
    data: { userData }
  } = useAuth();

  const [itemsPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pageParams, setPageParams] = useState<BaseQueryParams>({
    offset: 0,
    limit: itemsPerPage,
    deleted: false,
    includeArchived: true
  });

  const [tableData, setTableData] = useState<TwTableData[]>([]);
  const [isEditCarrierOpen, setIsEditCarrierOpen] = useState<boolean>(false);
  const [selectedCarrierId, setSelectedCarrierId] = useState<string>('');
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);

  const unformatString = useUnformatValue(Characters.number);
  const { addNotification } = useNotifications();
  // RTK Carrier querys
  const {
    data: carriers = { content: [], total: 0 },
    error: carriersError,
    isLoading
  } = useGetCarriersQuery(pageParams);
  const [deleteCarrier] = useDeleteCarrierMutation();

  /** Changes page */
  const handlePageChange = (value: number): void => {
    setPage(value);
  };

  const handleCreateUserOrResendInvite = async (
    carrier: CarrierResponse
  ): Promise<UserResponse | void> => {
    if (!carrier.email_address) {
      logRocketTrack('Team Management', {
        action: 'Create carrier',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Please enter email address',
        variant: 'error'
      });
      return Promise.reject();
    }
    if (carrier.first_name === null || carrier.last_name === null) {
      logRocketTrack('Team Management', {
        action: 'Create carrier',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Please enter point of contact first and last name',
        variant: 'error'
      });
      return Promise.reject();
    }

    if (carrier.twuser_id) {
      const userInvited = await UserService.invite(carrier.twuser_id);
      addNotification({
        message: 'Invite sent',
        variant: 'success'
      });
      return userInvited;
    }
    const user = await UserService.findByEmail(carrier.email_address);
    if (!user) {
      // if carrier does not have twuser_id (user), then create a user and send invite.
      const newUser = await UserService.create({
        first_name: carrier.first_name,
        last_name: carrier.last_name,
        carrier_name: carrier.name,
        email_address: carrier.email_address,
        phone: carrier.phone ? unformatString(carrier.phone) : undefined,
        send_email_invite: true,
        role: USER_ROLE.CARRIER
      });
      addNotification({
        message: 'Invite sent',
        variant: 'success'
      });
      return newUser;
    }
    await UserService.invite(user.id);
    addNotification({
      message: 'Invite sent',
      variant: 'success'
    });
    return user;
  };

  const columns: TwTableColumn[] = [
    { id: 'name', label: 'NAME', minWidth: 150 },
    { id: 'address', label: 'ADDRESS', minWidth: 150 },
    { id: 'email', label: 'EMAIL', minWidth: 150 },
    { id: 'phone', label: 'PHONE NUMBER', minWidth: 100 },
    { id: 'taxId', label: 'TAX ID', minWidth: 100 },
    { id: 'mcNumber', label: 'MC NUMBER', minWidth: 100 },
    { id: 'externalCompanyId', label: 'CARRIER ID', minWidth: 100 },
    { id: 'payTo', label: 'PAY TO', minWidth: 100 },
    { id: 'pointContact', label: 'POINT OF CONTACT', minWidth: 100 },
    { id: 'payRate', label: 'PAY RATE', minWidth: 150 },
    { id: 'menu', label: '', maxWidth: 10 }
  ];

  /**
   * Menu available for each carrier
   */
  const CarrierMenu: React.FC<{ carrier: CarrierResponse }> = ({
    carrier,
    carrier: { id }
  }: any): JSX.Element => {
    const options: TwGenericMenuOptions[] = [
      {
        label: 'Edit',
        action: (): void => {
          setSelectedCarrierId(id);
          setIsEditCarrierOpen(true);
        }
      },
      {
        label: 'Delete',
        className: 'carriers__delete',
        action: (): void => {
          setSelectedCarrierId(id);
          setDeleteConfirmationOpen(true);
        }
      }
    ];

    // only adding send invite option if not inviting self
    if (userData?.email_address !== carrier.email_address) {
      options.splice(1, 0, {
        label: 'Send Invite',
        action: (): Promise<UserResponse | void> => handleCreateUserOrResendInvite(carrier),
        disabled: !carrier.email_address
      });
    }

    return (
      <div className="carriers__options">
        <TWGenericMenu options={options} />
      </div>
    );
  };

  const getPayRate = (rate: string, type: CarrierPayRateType): string => {
    if (!rate) return '';

    const cleanRate = rate.replace(/(\%|\$)/gi, '');

    if (type === 'PERCENTAGE') {
      return `${cleanRate}%`;
    } else if (type === 'PER_HOUR') {
      return `$${cleanRate}/hr`;
    } else if (type === 'PER_MILE') {
      return `$${cleanRate}/mi`;
    }

    return `$${rate}`;
  };

  const transformCarriers = (carriers: CarrierResponse[]): void => {
    const carrierList: TwTableData[] = [];
    carriers.forEach((carrier) => {
      const tableItem: TwTableData = {
        name: carrier.name,
        address: carrier.location?.full_address ?? '',
        email: carrier.email_address ? carrier.email_address : '',

        phone: StringUtils.formatPhoneNumber(carrier.phone ?? '', carrier.phone_ext ?? ''),
        taxId: carrier.tax_id ?? '-',
        mcNumber: carrier.mc_number ?? '-',
        externalCompanyId: carrier.external_company_id ?? '-',
        payTo: carrier.pay_to ?? '-',
        pointContact: `${carrier.first_name ? carrier.first_name : ''}  ${
          carrier.last_name ? carrier.last_name : ''
        }`,
        payRate: getPayRate(carrier.pay_rate, carrier.default_pay_rate_type),
        menu: <CarrierMenu carrier={carrier} />
      };
      carrierList.push(tableItem);
    });
    setTableData(carrierList);
  };

  const handleEditCarrierClose = (): void => setIsEditCarrierOpen(false);

  /** Closes Delete confirmation dialog */
  const handleConfirmationClose = (): void => {
    setDeleteConfirmationOpen(false);
  };

  const handleDeleteCarrier = async (carrierId: string): Promise<void> => {
    try {
      deleteCarrier(carrierId)
        .unwrap()
        .then((response) => {
          setDeleteConfirmationOpen(false);
          addNotification({
            message: 'Carrier deleted successfully',
            variant: 'success'
          });
        });
    } catch (e: any) {
      console.error(e);
      logRocketTrack('Carrier Management', {
        action: e.message,
        severity: 'medium',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({ message: `Error: ${e.message}`, variant: 'error' });
    }
  };

  useEffect(() => {
    const newOffset = (page - 1) * itemsPerPage;
    setPageParams({ ...pageParams, offset: newOffset, limit: itemsPerPage });
  }, [page]);

  useEffect(() => {
    transformCarriers(carriers.content);
  }, [JSON.stringify(carriers.content)]);

  useEffect(() => {
    if (carriersError) {
      logRocketTrack('Carrier Management', {
        action: 'Open Carrier table',
        severity: 'medium',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Error fetching carrier list, please try again',
        variant: 'error'
      });
    }
  }, [carriersError]);

  return (
    <>
      {isLoading ? (
        <TwBackdrop isOpen={isLoading} />
      ) : (
        <Box sx={{ paddingTop: '1rem' }}>
          <TwTable columns={columns} data={tableData} />
          <TwPaginator
            currentPage={page}
            items={carriers.total}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />

          {/*  <EditCarrier
            open={isEditCarrierOpen}
            carrierId={selectedCarrierId}
            onClose={handleEditCarrierClose}
          /> */}
          <TwEditCompaniesModal
            open={isEditCarrierOpen}
            entity={CompaniesEntities.carrier}
            entityId={selectedCarrierId}
            onClose={handleEditCarrierClose}
          />

          <TwDialog
            size="xs"
            title="Delete carrier?"
            open={deleteConfirmationOpen}
            onClickClose={handleConfirmationClose}
            actions={[
              <TwButton
                key="1"
                type={TwButtonType.secondary}
                label="Cancel"
                action={handleConfirmationClose}
              />,
              <TwButton
                key="2"
                type={TwButtonType.negative}
                label="Delete"
                action={(): Promise<void> => handleDeleteCarrier(selectedCarrierId)}
              />
            ]}
          >
            <Box component="div" className="drivers__confirmation-dialog-content">
              <WarningSign />
              <span className="drivers__confirmation-dialog-text">
                Are you sure you want to delete this Carrier? You can’t undo this action.
              </span>
            </Box>
          </TwDialog>
        </Box>
      )}
    </>
  );
};

export default Carriers;
